import type {CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
import {CommentBox} from '@github-ui/comment-box/CommentBox'
import {validateTextField} from '@github-ui/entity-validators'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {Textarea} from '@primer/react'
import React, {useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {CommentBoxProps, GraphqlFragment, IssueFormElementRef, SharedElementProps} from '../types'
import {safeGetDefaultValue} from '../utils/default-value'
import {prefixMarkdownTextArea, prefixMarkdownTitle} from '../utils/markdown-conversion'
import {uniqueIdForElementInternal} from '../utils/session-storage'
import type {TextAreaElement_input$data, TextAreaElement_input$key} from './__generated__/TextAreaElement_input.graphql'
import {ElementWrapper} from './ElementWrapper'
import styles from './TextAreaElement.module.css'

export type TextAreaElementInternalProps = GraphqlFragment<TextAreaElement_input$data> & {
  type: 'textarea'
  index?: number
  ref?: React.Ref<IssueFormElementRef>
}

const DEBOUNCE_TIMEOUT_MS = 500 // 500ms debounce timeout for synchronizing user input with incoming default values

export type SharedTextAreaProps = CommentBoxProps &
  SharedElementProps & {onSave?: () => void; setIsFileUploading?: (value: boolean) => void}

type TextAreaElementProps = {
  elementRef: TextAreaElement_input$key
  index?: number
  ref?: React.Ref<IssueFormElementRef>
} & SharedTextAreaProps

export const TextAreaElement = ({ref, elementRef, index, sessionStorageKey, ...props}: TextAreaElementProps) => {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment TextAreaElement_input on IssueFormElementTextarea {
        __id
        itemId: id
        label
        descriptionHTML
        placeholder
        value
        required
        render
      }
    `,
    elementRef,
  )
  const elementStorageKey = uniqueIdForElementInternal(sessionStorageKey, '', data.label, data.__id)
  return (
    <TextAreaElementInternal
      {...data}
      {...props}
      index={index}
      ref={ref}
      type={'textarea'}
      sessionStorageKey={elementStorageKey}
    />
  )
}

export const TextAreaElementInternal = ({
  ref,
  itemId,
  sessionStorageKey,
  index: indexOfFormElement,
  label,
  descriptionHTML,
  placeholder,
  required,
  value: templateDefaultValue,
  render,
  subject,
  defaultValuesById,
  onSave,
  setIsFileUploading,
  onFieldChange,
}: TextAreaElementInternalProps & SharedTextAreaProps) => {
  const defaultValue = useMemo(
    () => safeGetDefaultValue({id: itemId, indexOfFormElement, value: templateDefaultValue, defaultValuesById}),
    [itemId, indexOfFormElement, templateDefaultValue, defaultValuesById],
  )

  const [textValue, setValue] = useSessionStorage<string>(sessionStorageKey, defaultValue)
  const [isUserTyping, setIsUserTyping] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Updates value with new default values from defaultValuesById
  useEffect(() => {
    const hasDefaultValues = defaultValuesById && Object.keys(defaultValuesById).length > 0
    if (onFieldChange && hasDefaultValues && !isUserTyping) {
      const newDefaultValue = safeGetDefaultValue({
        id: itemId,
        indexOfFormElement,
        value: templateDefaultValue,
        defaultValuesById,
      })

      // Only update if the new default value is different from current value
      // and it's not the result of the user's own typing being reflected back.
      // Normalize templateDefaultValue (which can be null from GraphQL) to match
      // the fallback used by safeGetDefaultValue (which returns value ?? '').
      const normalizedTemplateDefault = templateDefaultValue ?? ''
      if (newDefaultValue !== textValue && newDefaultValue !== normalizedTemplateDefault) {
        setValue(newDefaultValue)
      }
    }
  }, [
    defaultValuesById,
    indexOfFormElement,
    itemId,
    setValue,
    templateDefaultValue,
    isUserTyping,
    textValue,
    onFieldChange,
  ])

  // Handler for text changes that updates both local state and parent component
  const handleTextChange = (value: string) => {
    setIsUserTyping(true)
    setValue(value)

    if (onFieldChange) {
      // Since these are direct changes, they should always be sent up, regardless of
      // current state
      onFieldChange(itemId, indexOfFormElement, value)
    }

    // Clear existing timeout and set a new one
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Reset typing flag after a longer delay to prevent race conditions
    timeoutRef.current = setTimeout(() => {
      setIsUserTyping(false)
      timeoutRef.current = null
    }, DEBOUNCE_TIMEOUT_MS)
  }

  const textAreaCommentBoxRef = useRef<HTMLTextAreaElement & CommentBoxHandle>(null)
  const [validationResult, setValidationResult] = useState<string | undefined>(undefined)

  useImperativeHandle(
    ref,
    () => ({
      focus: () => textAreaCommentBoxRef.current?.focus(),
      markdown: () => prefixMarkdownTitle(label, prefixMarkdownTextArea(textValue, render)),
      initialMarkdown: () => prefixMarkdownTitle(label, prefixMarkdownTextArea(templateDefaultValue ?? '', render)),
      validate: () => {
        const validity = validateTextField(textValue, required ?? false)
        setValidationResult(validity.errorMessage)
        return validity.isValid
      },
      reset: () => {
        setValue(defaultValue)
        setValidationResult(undefined)
      },
      hasChanges: () => textValue !== defaultValue,
      getSessionStorageKey: () => sessionStorageKey,
      type: 'textarea' as const,
    }),
    [defaultValue, label, render, required, sessionStorageKey, setValue, templateDefaultValue, textValue],
  )

  useEffect(() => {
    // If we had a validation error and the user has fixed it, clear the error
    if (validationResult && validateTextField(textValue, required ?? false).isValid) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setValidationResult(undefined)
    }
  }, [required, textValue, validationResult])

  // Once https://github.com/github/collaboration-workflows-flex/issues/178 is fixed
  // we can go back to using the `ElementWrapper` which encapsulates the label, description and validation.
  // See `TextInputElement` for an example.
  return (
    <ElementWrapper
      label={label}
      required={required}
      description={descriptionHTML}
      validationResult={validationResult}
      className={styles.TextAreaElementContainer}
    >
      {({labelId, descriptionIds}) =>
        // If we have a render tag - we don't want to render the markdown editor.
        // See: https://docs.github.com/communities/using-templates-to-encourage-useful-issues-and-pull-requests/syntax-for-githubs-form-schema#textarea
        // Render tag is used to represent a code view - and therefore we should also style it as such.
        render ? (
          <Textarea
            aria-labelledby={labelId}
            aria-describedby={descriptionIds}
            ref={textAreaCommentBoxRef}
            placeholder={placeholder ?? undefined}
            value={textValue}
            className={styles.codeViewTextArea}
            onChange={e => {
              handleTextChange(e.target.value)
            }}
          />
        ) : (
          <CommentBox
            labelledBy={labelId}
            aria-describedby={descriptionIds}
            ref={textAreaCommentBoxRef}
            placeholder={placeholder ?? undefined}
            /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- TODO: [SECURITY] Potential XSS risk. Pre-existing infraction - DO NOT COPY. Must be fixed. */
            value={textValue as SafeHTMLString}
            onChange={e => {
              handleTextChange(e)
            }}
            onSave={onSave}
            saveButtonTrailingIcon={false}
            subject={subject}
            setIsFileUploading={setIsFileUploading}
          />
        )
      }
    </ElementWrapper>
  )
}

TextAreaElement.displayName = 'TextAreaElement'
TextAreaElementInternal.displayName = 'TextAreaElementInternal'
